'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import dayjs, { Dayjs } from 'dayjs'

import { useGetTripListQuery } from '@/src/api/rtkQuery/tripListApi'
import {
    NEGATIVE_TRIP_STATUSES,
    POSITIVE_TRIP_STATUSES,
    PROGRESS_TRIP_STATUSES
} from '@/src/constants/trips'
import { refreshAuthUser, selectAuth } from '@/src/redux/slices/authSlice'
import { useAppDispatch, useAppSelector } from '@/src/redux/store'
import { TripListCategory, TripSummary } from '@/src/types/trips'

const sanitizeTravelDate = (value?: string | null): Dayjs | null => {
    if (!value) return null
    const parsed = dayjs(value.replace('+0000', 'Z'))
    return parsed.isValid() ? parsed : null
}

const normalizeStatus = (status?: string): string => status?.toLowerCase().trim() || ''

const isActiveTrip = (trip: TripSummary): boolean => {
    const status = normalizeStatus(trip.bookingStatus)
    const travelDate = sanitizeTravelDate(trip.travelDate)

    const isNegativeStatus = NEGATIVE_TRIP_STATUSES.includes(status)
    const hasFutureTravelDate = travelDate
        ? travelDate.isSame(dayjs(), 'day') || travelDate.isAfter(dayjs(), 'day')
        : false

    if (hasFutureTravelDate) {
        return !isNegativeStatus
    }

    if (!travelDate) {
        return !isNegativeStatus && (PROGRESS_TRIP_STATUSES.includes(status) || POSITIVE_TRIP_STATUSES.includes(status))
    }

    return false
}

const mergeTrips = (existing: TripSummary[], newTrips: TripSummary[]): TripSummary[] => {
    const tripsMap = new Map<string, TripSummary>()
    existing.forEach(trip => tripsMap.set(trip.tripId, trip))
    newTrips.forEach(trip => {
        if (!tripsMap.has(trip.tripId)) {
            tripsMap.set(trip.tripId, trip)
        }
    })
    return Array.from(tripsMap.values())
}

const sortByTravelDateAsc = (a: TripSummary, b: TripSummary): number => {
    const dateA = sanitizeTravelDate(a.travelDate)
    const dateB = sanitizeTravelDate(b.travelDate)

    if (dateA && dateB) return dateA.valueOf() - dateB.valueOf()
    if (dateA && !dateB) return -1
    if (!dateA && dateB) return 1
    return a.tripName.localeCompare(b.tripName)
}

const sortByTravelDateDesc = (a: TripSummary, b: TripSummary): number => {
    const dateA = sanitizeTravelDate(a.travelDate)
    const dateB = sanitizeTravelDate(b.travelDate)

    if (dateA && dateB) return dateB.valueOf() - dateA.valueOf()
    if (dateA && !dateB) return -1
    if (!dateA && dateB) return 1
    return b.tripName.localeCompare(a.tripName)
}

export interface UseTripListReturn {
    isLoggedIn: boolean
    isLoading: boolean
    isFetching: boolean
    isError: boolean
    hasUnauthorizedError: boolean
    error: unknown
    tripsByCategory: Record<TripListCategory, TripSummary[]>
    tripCounts: Record<TripListCategory, number>
    hasMore: Record<TripListCategory, boolean>
    loadMore: (category: TripListCategory) => void
    isLoadingMore: Record<TripListCategory, boolean>
    refetch: () => void
}

export interface UseTripListOptions {
    isMultiBrand?: boolean
}

export const useTripList = (options?: UseTripListOptions): UseTripListReturn => {
    const { isMultiBrand = false } = options || {}
    const dispatch = useAppDispatch()
    const authState = useAppSelector(selectAuth)
    const isLoggedIn = authState.signedIn
    // Temporary then will revert to 'ALL' for multibrand later
    const fetchControl = 'MAIN'

    const [pageNo, setPageNo] = useState<Record<TripListCategory, number>>({
        upcoming: 0,
        past: 0
    })

    const [allUpcomingTrips, setAllUpcomingTrips] = useState<TripSummary[]>([])
    const [allPastTrips, setAllPastTrips] = useState<TripSummary[]>([])

    const [archivedStarted, setArchivedStarted] = useState(false)
    const [archivedPageNo, setArchivedPageNo] = useState(0)

    // Persist totals so hasMore stays stable while a new page is loading (data is undefined for the new cache key)
    const [knownTotals, setKnownTotals] = useState({ upcoming: 0, expiredCompleted: 0, archived: 0 })

    useEffect(() => {
        dispatch(refreshAuthUser())
    }, [dispatch])

    // ===== UPCOMING TAB: fetch upcoming trips =====
    const upcomingPageNo = pageNo.upcoming
    const {
        data: upcomingData,
        isLoading: isLoadingUpcoming,
        isFetching: isFetchingUpcoming,
        isError: isErrorUpcoming,
        error: errorUpcoming,
        refetch: refetchUpcoming
    } = useGetTripListQuery(
        {
            pageNo: upcomingPageNo,
            pageSize: 10,
            tripStatus: 'upcoming',
            fetchControl
        },
        { skip: !isLoggedIn }
    )

    // ===== PAST TAB: fetch expiredCompleted trips =====
    const pastPageNo = pageNo.past
    const {
        data: pastData,
        isLoading: isLoadingPast,
        isFetching: isFetchingPast,
        isError: isErrorPast,
        error: errorPast,
        refetch: refetchPast
    } = useGetTripListQuery(
        {
            pageNo: pastPageNo,
            pageSize: 10,
            tripStatus: 'expiredCompleted',
            fetchControl
        },
        { skip: !isLoggedIn }
    )

    // ===== PAST TAB: conditionally fetch archived trips when expiredCompleted is exhausted =====
    const {
        data: archivedData,
        isLoading: isLoadingArchived,
        isFetching: isFetchingArchived,
        isError: isErrorArchived,
        error: errorArchived,
        refetch: refetchArchived
    } = useGetTripListQuery(
        {
            pageNo: archivedPageNo,
            pageSize: 10,
            tripStatus: 'archived',
            fetchControl
        },
        { skip: !isLoggedIn || !archivedStarted }
    )

    useEffect(() => {
        if (upcomingData?.allTrips) {
            if (upcomingPageNo === 0) {
                setAllUpcomingTrips(upcomingData.allTrips)
            } else {
                setAllUpcomingTrips(prev => mergeTrips(prev, upcomingData.allTrips))
            }
            if (upcomingData.upcomingTotal != null) {
                setKnownTotals(prev => ({ ...prev, upcoming: upcomingData.upcomingTotal! }))
            }
        }
    }, [upcomingData, upcomingPageNo])

    useEffect(() => {
        if (pastData?.allTrips) {
            if (pastPageNo === 0) {
                setAllPastTrips(pastData.allTrips)
            } else {
                setAllPastTrips(prev => mergeTrips(prev, pastData.allTrips))
            }
            const total = pastData.expiredTotal ?? pastData.completedTotal ?? 0
            setKnownTotals(prev => ({ ...prev, expiredCompleted: total }))
        }
    }, [pastData, pastPageNo])

    useEffect(() => {
        if (!archivedStarted || !archivedData) return
        setAllPastTrips(prev => mergeTrips(prev, archivedData.allTrips ?? []))
        setKnownTotals(prev => ({ ...prev, archived: archivedData.archivedTotal ?? 0 }))
    }, [archivedData, archivedStarted])

    const isLoading = isLoadingUpcoming || isLoadingPast || isLoadingArchived
    const isFetching = isFetchingUpcoming || isFetchingPast || isFetchingArchived
    const isError = isErrorUpcoming || isErrorPast || isErrorArchived
    const error = errorUpcoming || errorPast || errorArchived
    const hasUnauthorizedError =
        (typeof (errorUpcoming as { status?: unknown })?.status === 'number' &&
            (errorUpcoming as { status?: number }).status === 401) ||
        (typeof (errorPast as { status?: unknown })?.status === 'number' &&
            (errorPast as { status?: number }).status === 401) ||
        (typeof (errorArchived as { status?: unknown })?.status === 'number' &&
            (errorArchived as { status?: number }).status === 401)

    const refetch = useCallback(() => {
        refetchUpcoming()
        refetchPast()
        if (archivedStarted) {
            refetchArchived()
        }
        setPageNo({ upcoming: 0, past: 0 })
        setArchivedPageNo(0)
        setArchivedStarted(false)
        setAllUpcomingTrips([])
        setAllPastTrips([])
        setKnownTotals({ upcoming: 0, expiredCompleted: 0, archived: 0 })
    }, [refetchUpcoming, refetchPast, refetchArchived, archivedStarted])

    const PAGE_SIZE = 10

    // Upcoming: single list, hasMore = loaded < total
    const hasMoreUpcoming =
        knownTotals.upcoming > 0 && allUpcomingTrips.length < knownTotals.upcoming

    // Past: expiredCompleted first, then archived. Hide "Load more" once both are exhausted.
    const hasMoreExpiredPages = (pastPageNo + 1) * PAGE_SIZE < knownTotals.expiredCompleted
    const hasMoreArchivedPages =
        archivedStarted && (archivedPageNo + 1) * PAGE_SIZE < knownTotals.archived
    const expiredDone = knownTotals.expiredCompleted > 0 && !hasMoreExpiredPages
    const showLoadMorePast =
        hasMoreExpiredPages ||
        (expiredDone && !archivedStarted) || // one "Load more" to switch to archived
        hasMoreArchivedPages

    const hasMore = useMemo(
        () => ({
            upcoming: hasMoreUpcoming,
            past: showLoadMorePast
        }),
        [hasMoreUpcoming, showLoadMorePast]
    )

    const tripCounts = useMemo(() => ({
        upcoming: knownTotals.upcoming,
        past: knownTotals.expiredCompleted + knownTotals.archived
    }), [knownTotals])

    // Derive isLoadingMore from RTK Query fetching state + page number,
    // so it stays true for the entire duration of the fetch.
    const isLoadingMore = useMemo<Record<TripListCategory, boolean>>(() => ({
        upcoming: isFetchingUpcoming && allUpcomingTrips.length > 0,
        past: ((isFetchingPast && allPastTrips.length > 0) || (isFetchingArchived && archivedStarted))
    }), [isFetchingUpcoming, isFetchingPast, isFetchingArchived, allUpcomingTrips.length, allPastTrips.length, archivedStarted])

    const loadMore = useCallback(
        (category: TripListCategory) => {
            if (isLoadingMore[category] || !hasMore[category]) return

            if (category === 'upcoming') {
                setPageNo(prev => ({ ...prev, upcoming: prev.upcoming + 1 }))
                return
            }

            // Past: next page of expired, or switch to archived, or next page of archived
            if ((pastPageNo + 1) * PAGE_SIZE < knownTotals.expiredCompleted) {
                setPageNo(prev => ({ ...prev, past: prev.past + 1 }))
            } else if (!archivedStarted) {
                setArchivedStarted(true)
                setArchivedPageNo(0)
            } else {
                setArchivedPageNo(prev => prev + 1)
            }
        },
        [isLoadingMore, hasMore, knownTotals.expiredCompleted, archivedStarted, pastPageNo]
    )

    const tripsByCategory = useMemo(() => {
        const upcomingTrips = [...allUpcomingTrips].sort(sortByTravelDateAsc)
        const pastTrips = [...allPastTrips].sort(sortByTravelDateDesc)

        return {
            upcoming: upcomingTrips,
            past: pastTrips
        }
    }, [allUpcomingTrips, allPastTrips])

    return {
        isLoggedIn,
        isLoading,
        isFetching,
        isError,
        hasUnauthorizedError,
        error,
        tripsByCategory,
        tripCounts,
        hasMore,
        loadMore,
        isLoadingMore,
        refetch
    }
}

export default useTripList
